import type { Chat, ChatParams, ChatPartial } from '../hooks/useChats/types';
import type { ChatContextHooksProps } from '../../context';

export type UseChatsValueResult = ReturnType<typeof useChatsValue>;

export function useChatsValue({ hooks }: ChatContextHooksProps) {
  const { chats } = hooks;
  // Init a new chat
  const initChat = useCallback(
    async (
      params: ChatPartial,
      handleInfo: (chatPartial: ChatPartial) => ResolveValue<ChatParams | void>
    ) => {
      const chatPartial: ChatPartial = {
        last_read_message_id: -1,
        ...params,
      };

      const chatParams = Object.assign(chatPartial, await handleInfo(chatPartial));

      if (chatParams && chatParams.name && chatParams.chat_id) {
        await chats.insertChats([chatParams]);
        return chatParams;
      }
    },
    [chats]
  );

  const createChat = useCallback(
    async (
      chatParams: ChatParams,
      initChat: (params: ChatParams) => ResolveValue<ChatParams | void>
    ) => {
      if (!chatParams.chat_id) {
        throw new Error('chat_id is required.');
      }
      return initChat(chatParams);
    },
    []
  );

  // Load a chat
  const loadChatByChatId = useCallback(
    async (
      params: ChatParams,
      initChat: (params: ChatParams) => ResolveValue<ChatParams | void>,
      handleEncryption: (params: ChatPartial) => ResolveValue<void>
    ) => {
      let isNew = false;
      const { chat_id } = params;
      let chat: ChatPartial | void = await chats.chatsState.getChat({
        chat_id,
      });
      if (!chat) {
        chat = await chats.chatsDB.getChat({ chat_id });
        if (chat) {
          await handleEncryption(chat);
          await chats.chatsState.insertChats([chat as Chat]);
        } else {
          chat = await initChat({ chat_id });
          isNew = true;
        }
      }
      return { chat, isNew };
    },
    [chats.chatsDB, chats.chatsState]
  );

  return useMemo(
    () => ({
      initChat,
      createChat,
      loadChatByChatId,
    }),
    [createChat, initChat, loadChatByChatId]
  );
}
